import { Box, Center, IconButton, Image, Spinner, useBoolean } from '@chakra-ui/react';
import { UPGRADE_REDIRECT_BIZ_LINK, UPGRADE_REDIRECT_LINK } from 'constants/links';
import { UPGRADE } from 'constants/mixpanel';
import { useIconData } from 'hooks/useIcon';
import { useIcons8Thumbnail } from 'hooks/useIcons8Thumbnail';
import { IconHit } from 'hooks/usePaginatedIconSearch';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { PermissionBadge, UserType, UserTypes, hasResourcePermission } from 'modules/User';
import { ReactElement } from 'react';
import SVG from 'react-inlinesvg';
import { useSWRConfig } from 'swr';
import { capitalizeStr } from 'utils/capitalizeStr';
import { setAnalyticsUpgradeFeatureProp } from 'utils/sessionStorage';
import { computeSvgViewBox } from 'utils/svg';

import { loadIcons8Icon } from './IconWidgetMenu.helpers';
import { iconViewBoxCache } from './iconViewBoxCache';

type onClickIconWidget = (id: string, viewBox: string) => void;

interface IconPreviewProps {
  id: string;
  userType: UserType;
  permission: IconHit['type'];
  onClickIconWidget: onClickIconWidget;
  onLoad: () => void;
  isMono?: boolean;
  iconPath?: string;
}

/**
 * ICON PREVIEW COMPONENT
 *
 * Component to render icon preview in the left icon widgetmenu
 */
export const IconPreview = ({
  id,
  userType,
  permission,
  onClickIconWidget,
  onLoad,
  isMono,
  iconPath,
}: IconPreviewProps) => {
  // Icons8 Icon
  if (iconPath) {
    return (
      <Icons8Icon
        id={id}
        isMono={!!isMono}
        userType={userType}
        permission={permission}
        onClickIconWidget={onClickIconWidget}
        onLoad={onLoad}
        iconPath={iconPath}
      />
    );
  }

  // S3 icon
  return (
    <S3Icon id={id} userType={userType} permission={permission} onClickIconWidget={onClickIconWidget} onLoad={onLoad} />
  );
};

interface Icons8IconProps {
  id: string;
  userType: UserType;
  permission: IconHit['type'];
  onClickIconWidget: onClickIconWidget;
  iconPath: string;
  isMono: boolean;
  onLoad: () => void;
}

/**
 * ICONS8 ICON PREVIEW COMPONENT
 *
 * Render icons8 preview using the icons8 img url
 */
const Icons8Icon = ({
  id,
  onClickIconWidget,
  userType,
  iconPath,
  isMono,
  permission,
  onLoad,
}: Icons8IconProps): ReactElement | null => {
  const { cache } = useSWRConfig();
  const { data: thumbnailBlob } = useIcons8Thumbnail(id);
  const [isLoading, setIsLoading] = useBoolean(false);

  if (!thumbnailBlob) {
    return null;
  }

  const thumbnailUrl = URL.createObjectURL(thumbnailBlob);

  /**
   * On click icon, load icon from icons8 URL and compute the viewbox
   * Can't rely on the viewbox from the API because all icons have additional padding
   */
  const onClickIcon = async () => {
    const cachedIcon = iconViewBoxCache.get(id);
    let viewBox = '';

    // Check cache
    if (cachedIcon) {
      viewBox = cachedIcon.viewBox;
    } else {
      // If not cached, load from Icons8 URL and compute viewBox so we don't have to recompute
      // everytime the icon is added from left panel
      setIsLoading.on();
      const iconData = await loadIcons8Icon(id);
      if (!iconData || !iconData.icon?.svg) {
        setIsLoading.off();
        return;
      }
      const { svg } = iconData.icon;

      // Set the icon data in the swr cache so we don't fetch it again
      // when the icon is added to the canvas from the useIconData hook used in IconWidget.tsx
      cache.set(id, iconData);

      viewBox = computeSvgViewBox(svg);
      iconViewBoxCache.add(id, { svg, viewBox });
      setIsLoading.off();
    }

    onClickIconWidget(id, viewBox);
  };

  return (
    // TODO remove border once verified on staging - will remove before release
    <IconThumbnail
      icon={<Image src={thumbnailUrl} alt={id} w='full' h='full' objectFit='contain' onLoad={onLoad} />}
      buttonAriaLabel='Add new icon'
      onClick={onClickIcon}
      userType={userType}
      permission={permission}
      isLoading={isLoading}
    />
  );
};

const iconStyle = { width: '100%', height: '100%' };

interface S3IconProps {
  id: string;
  userType: UserType;
  permission: IconHit['type'];
  onClickIconWidget: onClickIconWidget;
  onLoad: () => void;
}

/**
 * S3 ICON PREVIEW COMPONENT
 *
 * Renders icons from S3 bucket - these icons are pulled from Algolia search
 */
const S3Icon = ({ id, userType, permission, onClickIconWidget, onLoad }: S3IconProps): ReactElement | null => {
  const iconData = useIconData({ iconId: id });

  if (!iconData) return null;

  const { svg, description, viewBox = '' } = iconData;
  const title = 'icon';
  const buttonAriaLabel = 'Add new icon';

  const allowAccess = hasResourcePermission(userType, permission);

  const onClickIcon = () => {
    if (allowAccess) onClickIconWidget(id, viewBox);
    else {
      const userPlanStr = userType === 'default' ? 'Free' : userType;
      const iconPermissionStr = permission === 'default' ? 'Free' : permission;
      const mpFeatureStr = `${capitalizeStr(iconPermissionStr)} Icons`;

      Mixpanel.track(UPGRADE.eventName, {
        Feature: mpFeatureStr,
        'User Plan': capitalizeStr(userPlanStr),
      });

      setAnalyticsUpgradeFeatureProp(mpFeatureStr);
      const link = permission === UserTypes.Business ? UPGRADE_REDIRECT_BIZ_LINK : UPGRADE_REDIRECT_LINK;

      window.open(link, '_self');
    }
  };

  return (
    <IconThumbnail
      icon={
        <SVG
          uniquifyIDs={true}
          title={title}
          description={description}
          onLoad={onLoad}
          role='img'
          style={iconStyle}
          src={svg}
        />
      }
      buttonAriaLabel={buttonAriaLabel}
      onClick={onClickIcon}
      userType={userType}
      permission={permission}
    />
  );
};

interface IconThumbnailProps {
  icon: ReactElement;
  onClick: () => void;
  buttonAriaLabel: string;
  userType: UserType;
  permission: IconHit['type'];
  isLoading?: boolean;
}

/**
 * THUMBNAIL COMPONENT
 *
 * Renders icon thumbnail with permisison badge
 */
const IconThumbnail = ({
  icon,
  onClick,
  buttonAriaLabel,
  userType,
  permission,
  isLoading,
}: IconThumbnailProps): ReactElement => {
  const showBadge = !hasResourcePermission(userType, permission);

  return (
    <Box position={'relative'}>
      <IconButton
        width='72px'
        height='72px'
        p={2}
        aria-label={buttonAriaLabel}
        onClick={onClick}
        variant={'icon-btn-side-menu'}
        icon={icon}
      />
      {showBadge && (
        <PermissionBadge
          permission={permission}
          position={'absolute'}
          top={0}
          left={'50%'}
          transform={'translate(-50%, 0)'}
        />
      )}
      {isLoading && (
        <Center position='absolute' borderRadius='base' bg='black-alpha.500' w='72px' h='72px' top={0} left={0}>
          <Spinner size='md' color='brand.500' />
        </Center>
      )}
    </Box>
  );
};
